import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Compass, Inbox } from "lucide-react";
import { Link } from "react-router-dom";

import { SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
// ★ FSD:features 互不 import,跨 feature 的登录态只许经 `@/contexts` 这道过渡门(ADR-0013 F1)。
import { useAuth } from "@/contexts/AuthContext";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { Button } from "@/shared/ui/button";
import { Pagination } from "@/shared/ui/pagination";
import { Skeleton } from "@/shared/ui/skeleton";

import {
  ACQ_PRODUCT_NONE,
  acqListsRevision,
  acqService,
  subscribeAcqLists,
  UNASSIGNED_ASSIGNEE_ID,
  type AcqFeedbackCode,
  type AcqOpportunityCard,
  type AcqOpportunityPage,
  type AcqOpportunityQuery,
  type AcqState,
} from "../api/acqService";
import {
  FEEDBACK_LABEL,
  FILTERABLE_SOURCES,
  INTENT_TYPE_ORDER,
  intentTypeLabel,
  sourceLabel,
  STATE_ORDER,
  stateLabel,
} from "../model/acqLabels";
import { useAcqActions } from "../model/useAcqActions";
import { useProductParam } from "../model/useProductParam";
import { useTenantProducts } from "../model/useTenantProducts";
import { AcqShell } from "./AcqShell";
import {
  AcqAssigneePicker,
  ASSIGNEE_MINE,
  ASSIGNEE_UNASSIGNED,
} from "./components/AcqAssigneePicker";
import { AcqPicker } from "./components/AcqPicker";
import { LeadDrawer } from "./components/LeadDrawer";
import { BatchUnlockBar } from "./components/BatchUnlockBar";
import { LeadsTable } from "./components/LeadsTable";
import { ProductTabs, type ProductTabItem } from "./components/ProductTabs";
import { errorText } from "@/shared/lib/apiErrors";

const PAGE_SIZE = 20;

/**
 * 线索库(契约 §4 `GET /acq/opportunities`,0917 批次二改表格)。
 *
 * 今日商机是"今天该干什么",这一页是**"我那一堆线索现在什么样"** ——
 * 所以它是一张能横着比的表,不是一墙卡(契约 §13.6)。整张卡搬进右侧抽屉。
 * 不做批量导出(契约 §9④)。
 */
export default function LeadsPage() {
  const [data, setData] = useState<AcqOpportunityPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<AcqState | "">("");
  const [source, setSource] = useState("");
  const [feedbackCode, setFeedbackCode] = useState<AcqFeedbackCode | "">("");
  /**
   * 按产品看(契约 §16.7:「线索库的产品下拉改成同一排产品页签」)。
   * ★ 与另外三页共用 `?product=` —— 从今日「看全部线索」跳过来时,那条产品线跟着过来。
   */
  const [productCode, setProductCode] = useProductParam();
  const [intentType, setIntentType] = useState("");
  const [assignee, setAssignee] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  /** 1015 批量补全:当前页勾选的商机。翻页 / 换筛子就清掉 —— 勾的是"这一屏看到的那几条"。 */
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const { book } = usePriceBook();
  const listPrice = book.unit(SKU.acqUnlock) ?? book.unit(SKU.acqUnlock, "default");

  const { user, effectiveRole } = useAuth();
  const canAssign = isTenantManagerPlus(effectiveRole);
  const meId = user?.userId ?? null;
  const { products, nameOf } = useTenantProducts();

  /**
   * 「分派」筛子 → `assigneeId`。
   * ★ **未分派传 0**(`UNASSIGNED_ASSIGNEE_ID`):见 acqService 里那段注释 ——
   *   传一个后端不认识的新参数,失败形态是"静默返回全部行";传 0 至少是空列表,错得看得见。
   * ★ 「我的」在没登录 id 时**不发筛子**:发一个 `assigneeId=undefined` 等于不筛,
   *   而用户以为自己在看"我的" —— 宁可这一档点不动,也不给一份贴错标签的名单。
   */
  const assigneeId = useMemo<number | null>(() => {
    if (assignee === ASSIGNEE_UNASSIGNED) return UNASSIGNED_ASSIGNEE_ID;
    if (assignee === ASSIGNEE_MINE) return meId;
    if (assignee) return Number(assignee);
    return null;
  }, [assignee, meId]);

  /** 「列表旧了」的信号(契约 §14.9)—— 见 TodayPage 里同一段注释。 */
  const listsRev = useSyncExternalStore(subscribeAcqLists, acqListsRevision, () => 0);

  const query = useMemo<AcqOpportunityQuery>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      state,
      source,
      feedbackCode,
      productCode,
      intentType,
      assigneeId,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listsRev 是"重取一次"的信号,不进 query body
    [page, state, source, feedbackCode, productCode, intentType, assigneeId, listsRev]
  );

  const load = useCallback(async (q: AcqOpportunityQuery) => {
    try {
      setData(await acqService.opportunities(q));
      setError(null);
    } catch (e) {
      setError(errorText(e, { fallbackTitle: "加载失败" }));
    }
  }, []);

  useEffect(() => {
    void load(query);
    setSelected(new Set());
  }, [load, query]);

  const toggleSelect = useCallback((id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const toggleAll = useCallback((ids: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const patch = useCallback((c: AcqOpportunityCard) => {
    setData((prev) =>
      prev ? { ...prev, items: prev.items.map((x) => (x.id === c.id ? c : x)) } : prev
    );
  }, []);
  const { busyId, take, unlock, feedback, takeAction, assign, claim } = useAcqActions(patch);

  /**
   * 页签的数据:线索库这一页**只有名字没有数**。
   * ★★ `productSummary.openCount` 是"非终态卡数",而这张表里连 WON / LOST 都在 ——
   *   把那个数摆在这儿,用户点进去看到的条数与页签上写的对不上,而两个数**都没错**,
   *   只是口径不同。宁可不报。
   */
  const productTabs = useMemo<ProductTabItem[]>(
    () => [
      ...products.map((p) => ({ code: p.nodeCode, name: p.name, count: null })),
      // ★ 「未指定产品」这一档(契约 §16.2 api 落地形状:`/opportunities` 认哨兵 `__none__`)。
      //   这一页没有 `productSummary`,所以**不知道有没有**这种卡 —— 照摆:它是个筛子,
      //   筛空了有现成的空态("这几个筛子下没有线索"),而藏起来的话那批没绑产品的卡
      //   在整排页签上一格都不属于,加起来对不上而且没人会发现。
      { code: "", name: "未指定产品", count: null },
    ],
    [products]
  );

  const total = data?.total ?? 0;
  const openCard = data?.items.find((c) => c.id === openId) ?? null;
  const dirty = !!(state || source || feedbackCode || productCode || intentType || assignee);
  /** 除产品之外还开着的筛子 —— 空态的说法按它分岔(见下面那段注释)。 */
  const otherFilters = !!(state || source || feedbackCode || intentType || assignee);
  /**
   * 选中页签那个**具体**产品(契约 §19.3 的空态要把名字写进那句话)。
   * ★ 这一页没有 `productSummary`,名字只能从产品树取;`nameOf` 查不到时回落成 code 本身,
   *   所以这一格永远不是空串。「全部」与哨兵 `__none__` 都不算一个产品。
   */
  const focusProduct = useMemo(() => {
    if (!productCode || productCode === ACQ_PRODUCT_NONE) return null;
    return { code: productCode, name: nameOf(productCode) };
  }, [productCode, nameOf]);

  const onAssign = useCallback(
    (id: string, aid: number | null, displayName?: string) => void assign(id, aid, displayName),
    [assign]
  );
  const onClaim = useCallback((id: string) => void claim(id), [claim]);

  return (
    <AcqShell subtitle={data == null ? "正在加载线索…" : `共 ${total} 条线索`}>
      {/* ★ `max-w-3xl` 删掉(契约 §12.10):线索库是一面能扫的墙,不是一条要从上读到下的单子。 */}
      <div className="w-full space-y-4">
        {/* ── 产品页签(契约 §16.7):产品是主轴,不是第四个筛子 ─────────────
            ★ 这一排与另外三页逐字同形、共用 `?product=` —— 用户在四页之间跳的时候,
            "我在看哪条产品线"这件事不该每跳一次就重设一遍。 */}
        <ProductTabs
          items={productTabs}
          value={productCode}
          onChange={(code) => {
            setProductCode(code);
            setPage(1);
          }}
          allowNone
        />

        {/* ── 筛子:状态 / 来源 / 反馈 / 意向 / 分派(契约 §13.6)──────────── */}
        <div className="flex flex-wrap items-center gap-2">
          <AcqPicker
            label="状态"
            value={state}
            onChange={(v) => {
              setState(v as AcqState | "");
              setPage(1);
            }}
            options={STATE_ORDER.map((s) => ({ value: s, label: stateLabel(s) }))}
          />
          <AcqPicker
            label="来源"
            value={source}
            onChange={(v) => {
              setSource(v);
              setPage(1);
            }}
            options={FILTERABLE_SOURCES.map((s) => ({ value: s, label: sourceLabel(s) }))}
          />
          <AcqPicker
            label="反馈"
            value={feedbackCode}
            onChange={(v) => {
              setFeedbackCode(v as AcqFeedbackCode | "");
              setPage(1);
            }}
            options={(Object.keys(FEEDBACK_LABEL) as AcqFeedbackCode[]).map((c) => ({
              value: c,
              label: FEEDBACK_LABEL[c],
            }))}
          />
          {/* 0917 批次三(契约 §14.3):按「这个人想干什么」筛 —— 与状态、来源正交。 */}
          <AcqPicker
            label="意向"
            value={intentType}
            onChange={(v) => {
              setIntentType(v);
              setPage(1);
            }}
            options={INTENT_TYPE_ORDER.map((c) => ({ value: c, label: intentTypeLabel(c) }))}
          />
          {/* ★ 0917 批次四(契约 §15.3):人名下拉换成系统那一个部门树 picker ——
              这一页原来自己拉了一份 `/tenant/users` 拼下拉,和审计页、派单弹窗三种长相。
              ★ 坐席那一档仍然有这个筛子(他要「只看我的」),只是没有"按别人筛"那一段。 */}
          <AcqAssigneePicker
            value={assignee}
            meId={meId}
            canPickPerson={canAssign}
            onChange={(v) => {
              setAssignee(v);
              setPage(1);
            }}
          />
          {dirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setState("");
                setSource("");
                setFeedbackCode("");
                setProductCode("");
                setIntentType("");
                setAssignee("");
                setPage(1);
              }}
            >
              清空筛选
            </Button>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {data == null && !error && <Skeleton className="h-48 w-full rounded-xl" />}

        {/* ★★ 0918 批次八(契约 §19.3):**产品页签下的 0 条要给出口** —— 与今日页同一条
            (那儿的文案是「还没有商机」,这儿是「还没有线索」)。
            ★ 同样**只在产品是唯一筛子**时这么说:还开着状态/反馈/分派的时候,0 条多半是
              筛子的事,劝人再去发现一次是错的建议。 */}
        {data != null && data.items.length === 0 && focusProduct && !otherFilters && (
          <div className="rounded-xl border border-dashed bg-card px-6 py-12 text-center">
            <Compass className="mx-auto h-8 w-8 text-primary" />
            <p className="mt-3 text-base font-bold">「{focusProduct.name}」还没有线索</p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
              {/* ★ 整句一个字符串:JSX 断行会在句中留一个空格(见今日页同一处注释)。 */}
              {
                "这条产品线一条线索都没有。去「去哪里找」按这个产品发现一次，找回来的卡会直接落在这个页签下。"
              }
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button asChild size="sm">
                <Link to={`/ai-huoke/discover?product=${encodeURIComponent(focusProduct.code)}`}>
                  <Compass className="h-4 w-4" />
                  按「{focusProduct.name}」发现一次
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setProductCode("");
                  setPage(1);
                }}
              >
                看全部产品
              </Button>
            </div>
          </div>
        )}

        {data != null && data.items.length === 0 && !(focusProduct && !otherFilters) && (
          <div className="rounded-xl border border-dashed bg-card px-6 py-12 text-center">
            <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-semibold">
              {dirty ? "这几个筛子下没有线索" : "这里还没有线索"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {dirty ? (
                "换个筛子看看 —— 线索还在，只是不在这个条件里。"
              ) : (
                <>
                  {/* 0917:「每天早 9 点推来的商机」那句撤了 —— 后端还没有定时与企微推送,
                      页面不许承诺后端没做的事。接上之后再放回来。 */}
                  从「今日商机」跟过的会落进这里。
                  <Link to="/ai-huoke" className="ml-1 font-semibold text-primary hover:underline">
                    看今日商机
                  </Link>
                </>
              )}
            </p>
          </div>
        )}

        <BatchUnlockBar
          selectedIds={[...selected]}
          priceMin={data?.items[0]?.unlockCredits ?? listPrice ?? null}
          priceMax={data?.items[0]?.unlockCreditsMax ?? null}
          onClear={() => setSelected(new Set())}
          onDone={() => void load(query)}
        />

        {data != null && data.items.length > 0 && (
          <LeadsTable
            selectedIds={selected}
            onToggleSelect={toggleSelect}
            onToggleAll={toggleAll}
            items={data.items}
            canAssign={canAssign}
            meId={meId}
            busyId={busyId}
            activeId={openId}
            onOpen={(c) => setOpenId(c.id)}
            onAssign={onAssign}
            onClaim={onClaim}
            // 契约 §19.3:表里那几枚产品 chip 点一下就切到那个产品的页签(并回第一页)。
            onPickProduct={(code) => {
              setProductCode(code);
              setPage(1);
            }}
          />
        )}

        {/* ★ 0917 批次四(契约 §15.3):换成全平台那一个 `@/shared/ui/pagination`
            (客服会话页同一套用法)。手搓的"上一页/下一页"少了首末页与跳页 ——
            一页 20 条、几百条线索的时候,翻到第 9 页要点八下。
            ★ `page` 那边是 1-based,共享组件是 0-based,所以进出各错一位。 */}
        {/* ★ 只有一页时不摆翻页条:一条"共 1 条 · 1/1"的翻页器是纯噪音,
            而"一共多少条"这一页顶上已经说过了。 */}
        {data != null && total > PAGE_SIZE && (
          <Pagination
            page={page - 1}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={(p) => setPage(p + 1)}
          />
        )}
      </div>

      <LeadDrawer
        card={openCard}
        canAssign={canAssign}
        meId={meId}
        busy={!!openCard && busyId === openCard.id}
        unlockPrice={openCard?.unlockCredits ?? listPrice}
        onOpenChange={(o) => !o && setOpenId(null)}
        onTake={(id) => void take(id)}
        onUnlock={(id) => void unlock(id)}
        onFeedback={(id, code) => void feedback(id, code)}
        onAction={(c) => void takeAction(c)}
        onAssign={onAssign}
        onClaim={onClaim}
      />
    </AcqShell>
  );
}
